/**
 * 按考试类型获取题目难度选项（生题、导入、专项筛选等）
 *
 * 分层（刻意）：
 * - 官方枚举 / levels.ts：完整标尺与文案（含非目标档，供历史题展示）
 * - `values`：产品窗口（专项入口、Admin 默认下拉、`isLevelSupportedForExam`）
 * - `generationValues`：生题常用档（可 ⊆ values）
 *
 * 产品基准：**法语 TCF = A1–B2**；其它考试按本包 `cefrEquivalent`（或同级备考定位）
 * 映射到等价窗口，砍掉更低的「无能力」档与 C 档尖端。
 */

import { TCFLevel } from './exams/tcf/types';
import { getLevelDescription as getTCFLevelDesc } from './exams/tcf/levels';
import { DELELevel } from './exams/dele/types';
import { getLevelDescription as getDELELevelDesc } from './exams/dele/levels';
import { SIELELevel } from './exams/siele/types';
import { getLevelDescription as getSIELELevelDesc } from './exams/siele/levels';
import { IELTSLevel } from './exams/ielts/types';
import { getLevelDescription as getIELTSLevelDesc } from './exams/ielts/levels';
import { TOEFLLevel } from './exams/toefl/types';
import { getLevelDescription as getTOEFLLevelDesc } from './exams/toefl/levels';
import { JLPTLevel } from './exams/jlpt/types';
import { getLevelDescription as getJLPTLevelDesc } from './exams/jlpt/levels';
import { TOPIKLevel } from './exams/topik/types';
import { getLevelDescription as getTOPIKLevelDesc } from './exams/topik/levels';

type LevelOption = { value: string; label: string };

/** 展示用：官方等级码 · 能力描述（描述已含码时不重复） */
export function formatLevelOptionLabel(code: string, name: string | undefined): string {
  const trimmed = name?.trim();
  if (!trimmed) return code;
  if (trimmed === code || trimmed.includes(code)) return trimmed;
  return `${code} · ${trimmed}`;
}

/** IELTS 下拉展示用 Band 文案；option.value 仍为枚举原始值（如 "6" / "6.5"） */
export function formatIeltsBandLabel(value: string): string {
  const n = Number(value);
  if (Number.isNaN(n)) return `Band ${value}`;
  return Number.isInteger(n) ? `Band ${n}.0` : `Band ${value}`;
}

/**
 * 产品难度基准（法语 TCF）。
 * CEFR 系考试直接复用；非 CEFR 考试映射到等价窗口。
 */
const CEFR_A1_B2_PRODUCT_LEVELS = ['A1', 'A2', 'B1', 'B2'] as const;

/**
 * IELTS ≈ 法语 A1–B2 窗口的实用下沿：从 Band 4 起（≈B1 入门），
 * 至 Band 6.5（B2）；不含 ≤3.5 与 C1+（≥7）。
 * 生题与产品窗口同一套，不再另开 generationValues。
 */
const IELTS_PRODUCT_LEVELS = ['4', '4.5', '5', '5.5', '6', '6.5'] as const;

/**
 * TOEFL ≈ A1–B2（按 toefl/levels cefrEquivalent）：
 * beginner=A1 … high-intermediate=B2；不含 advanced(C1)+。
 */
const TOEFL_PRODUCT_LEVELS = [
  'beginner',
  'low-beginner',
  'high-beginner',
  'low-intermediate',
  'intermediate',
  'high-intermediate',
] as const;

/** JLPT ≈ A1–B2：N5–N2（N1≈C1，砍掉） */
const JLPT_PRODUCT_LEVELS = ['N5', 'N4', 'N3', 'N2'] as const;

/** TOPIK ≈ A1–B2：1–4（5–6≈C，砍掉） */
const TOPIK_PRODUCT_LEVELS = ['1', '2', '3', '4'] as const;

const EXAM_LEVEL_MAP: Record<
  string,
  {
    values: readonly string[];
    /** 缺省则生题下拉 = 全部 values */
    generationValues?: readonly string[];
    getLabel: (value: string) => string;
  }
> = {
  tcf: {
    values: CEFR_A1_B2_PRODUCT_LEVELS,
    getLabel: (v) => formatLevelOptionLabel(v, getTCFLevelDesc(v as TCFLevel)?.name),
  },
  dele: {
    values: CEFR_A1_B2_PRODUCT_LEVELS,
    getLabel: (v) => formatLevelOptionLabel(v, getDELELevelDesc(v as DELELevel)?.name),
  },
  siele: {
    values: CEFR_A1_B2_PRODUCT_LEVELS,
    getLabel: (v) => formatLevelOptionLabel(v, getSIELELevelDesc(v as SIELELevel)?.name),
  },
  ielts: {
    values: IELTS_PRODUCT_LEVELS,
    getLabel: (v) =>
      formatLevelOptionLabel(
        formatIeltsBandLabel(v),
        getIELTSLevelDesc(v as IELTSLevel)?.name
      ),
  },
  toefl: {
    values: TOEFL_PRODUCT_LEVELS,
    getLabel: (v) => {
      const desc = getTOEFLLevelDesc(v as TOEFLLevel);
      const code = desc?.totalScoreRange ? `${v} (${desc.totalScoreRange})` : v;
      return formatLevelOptionLabel(code, desc?.name);
    },
  },
  jlpt: {
    values: JLPT_PRODUCT_LEVELS,
    getLabel: (v) => formatLevelOptionLabel(v, getJLPTLevelDesc(v as JLPTLevel)?.name),
  },
  topik: {
    values: TOPIK_PRODUCT_LEVELS,
    getLabel: (v) => {
      const desc = getTOPIKLevelDesc(v as TOPIKLevel);
      const code = `TOPIK ${v}`;
      return formatLevelOptionLabel(code, desc?.name);
    },
  },
};

/**
 * 获取某考试类型的产品难度取值列表（专项筛选 / 校验 / Admin 默认下拉）
 */
export function getSupportedLevelsForExam(examType: string): string[] {
  const key = examType?.toLowerCase?.();
  const entry = key ? EXAM_LEVEL_MAP[key] : undefined;
  return entry ? [...entry.values] : [];
}

/**
 * 获取某考试类型生题常用难度取值（可窄于产品窗口）
 */
export function getGenerationLevelsForExam(examType: string): string[] {
  const key = examType?.toLowerCase?.();
  const entry = key ? EXAM_LEVEL_MAP[key] : undefined;
  if (!entry) return [];
  if (entry.generationValues?.length) return [...entry.generationValues];
  return [...entry.values];
}

function buildLevelOptionsFromValues(
  entry: (typeof EXAM_LEVEL_MAP)[string],
  values: readonly string[],
  currentLevel?: string
): LevelOption[] {
  const options: LevelOption[] = [{ value: '', label: '不限定' }];
  const seen = new Set<string>();
  for (const value of values) {
    if (seen.has(value)) continue;
    seen.add(value);
    options.push({ value, label: entry.getLabel(value) });
  }
  const extra = currentLevel?.trim();
  // 编辑态：当前值若仍在产品窗口但不在本下列表，保留一行（勿复活已砍掉的非目标档）
  if (extra && !seen.has(extra) && entry.values.includes(extra)) {
    options.push({ value: extra, label: entry.getLabel(extra) });
  }
  return options;
}

/**
 * 获取某考试类型的产品难度下拉选项（含「不限定」），用于专项筛选/展示等
 */
export function getLevelOptionsForExam(examType: string): LevelOption[] {
  const key = examType?.toLowerCase?.();
  const entry = key ? EXAM_LEVEL_MAP[key] : undefined;
  if (!entry) return [{ value: '', label: '不限定' }];
  return buildLevelOptionsFromValues(entry, entry.values);
}

/**
 * 生题表单难度下拉：常用档 +「不限定」；若当前值在产品窗口但不在常用档，仍保留该项
 */
export function getLevelOptionsForGeneration(
  examType: string,
  currentLevel?: string
): LevelOption[] {
  const key = examType?.toLowerCase?.();
  const entry = key ? EXAM_LEVEL_MAP[key] : undefined;
  if (!entry) return [{ value: '', label: '不限定' }];
  const values = entry.generationValues?.length ? entry.generationValues : entry.values;
  return buildLevelOptionsFromValues(entry, values, currentLevel);
}

/**
 * 判断某考试类型下某难度值是否在产品窗口内（空串 = 不限定，合法）
 */
export function isLevelSupportedForExam(examType: string, level: string): boolean {
  const levels = getSupportedLevelsForExam(examType);
  return level === '' || levels.includes(level);
}
